"use client";

import { useMemo } from "react";
import { useZones } from "@/hooks/use-zones";
import { QuickAddSelect } from "./quick-add-select";
import type { Zone } from "@/lib/types";

function buildChildrenMap(zones: Zone[]) {
  const map = new Map<number | null, Zone[]>();
  for (const zone of zones) {
    const parent = zone.parent_zone ?? null;
    if (!map.has(parent)) {
      map.set(parent, []);
    }
    map.get(parent)!.push(zone);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return map;
}

function collectDescendantIds(zones: Zone[], rootId: number): number[] {
  const childrenByParent = buildChildrenMap(zones);
  const ids: number[] = [rootId];
  let i = 0;
  while (i < ids.length) {
    const children = childrenByParent.get(ids[i]) ?? [];
    for (const child of children) {
      ids.push(child.id);
    }
    i += 1;
  }
  return ids;
}

export interface ZonePickerProps {
  locationId: number;
  value: number | null;
  onChange: (zoneId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  excludeTreeRootId?: number;
}

export function ZonePicker({
  locationId,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  excludeTreeRootId,
}: ZonePickerProps) {
  const { data, isLoading } = useZones(
    { location: locationId, page_size: 1000 },
    !!locationId
  );

  const options = useMemo(() => {
    const all = data?.results ?? [];
    if (!excludeTreeRootId) return all;
    const excludeIds = new Set(collectDescendantIds(all, excludeTreeRootId));
    return all.filter((z) => !excludeIds.has(z.id));
  }, [data, excludeTreeRootId]);

  const selected = useMemo(
    () => options.find((z) => z.id === value) ?? null,
    [options, value]
  );

  return (
    <QuickAddSelect
      value={selected}
      onChange={(zone) => onChange(zone?.id ?? null)}
      options={options}
      getOptionValue={(z) => z.id}
      getOptionLabel={(z) => z.path}
      isLoading={isLoading}
      placeholder={placeholder ?? "Select a zone"}
      disabled={disabled}
      className={className}
    />
  );
}
